import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { Column } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import type { ReactNode } from 'react';

const SORT_TOOLTIP_UNSORTED = 'Sort';
const SORT_TOOLTIP_ASC = 'Sorted ascending';
const SORT_TOOLTIP_DESC = 'Sorted descending';

function sortTooltip(sortState: false | 'asc' | 'desc'): string {
  if (sortState === 'asc') return SORT_TOOLTIP_ASC;
  if (sortState === 'desc') return SORT_TOOLTIP_DESC;
  return SORT_TOOLTIP_UNSORTED;
}

function SortDirectionIcon({
  sortState,
  className,
}: {
  sortState: false | 'asc' | 'desc';
  className?: string;
}) {
  if (sortState === 'asc') {
    return (
      <ArrowUp
        className={cn('h-4 w-4 shrink-0 text-neutral-900', className)}
        aria-hidden
      />
    );
  }
  if (sortState === 'desc') {
    return (
      <ArrowDown
        className={cn('h-4 w-4 shrink-0 text-neutral-900', className)}
        aria-hidden
      />
    );
  }
  return (
    <ArrowUpDown
      className={cn('h-4 w-4 shrink-0 text-neutral-400', className)}
      aria-hidden
    />
  );
}

export type ColumnSortVisualState = false | 'asc' | 'desc';

/**
 * Inline sort control for non–TanStack tables (custom sort state).
 * Label is left-aligned; sort icon is right-aligned.
 */
export function ControlledSortColumnHeader({
  label,
  sortDirection,
  onSortClick,
  className,
  disabled,
}: {
  label: ReactNode;
  sortDirection: ColumnSortVisualState;
  onSortClick: () => void;
  className?: string;
  disabled?: boolean;
}) {
  const tooltip = sortTooltip(sortDirection);

  if (disabled) {
    return (
      <div
        className={cn(
          'flex w-full min-w-0 items-center justify-between gap-2',
          className
        )}
      >
        <div className="min-w-0 flex-1 text-left">{label}</div>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onSortClick}
          className={cn(
            'flex w-full min-w-0 items-center justify-between gap-2 rounded text-left',
            'hover:bg-neutral-50/80 -mx-0.5 px-0.5',
            className
          )}
        >
          <div className="min-w-0 flex-1 text-left">{label}</div>
          <SortDirectionIcon sortState={sortDirection} />
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Full-width TanStack header: title left, sort icon right.
 */
export function TanstackSortableColumnHeader<TData>({
  column,
  children,
  iconOnly,
  accessibleTitle,
}: {
  column: Column<TData, unknown>;
  children?: ReactNode;
  /** Narrow columns (e.g. priority): icon only, right-aligned */
  iconOnly?: boolean;
  /** Required when iconOnly is true (visible aria-label / tooltips) */
  accessibleTitle?: string;
}) {
  if (!column.getCanSort()) {
    return (
      <div
        className={cn(
          'flex w-full min-w-0 items-center',
          iconOnly && 'justify-end'
        )}
      >
        {children}
      </div>
    );
  }

  const rawSorted = column.getIsSorted();
  const sortState: ColumnSortVisualState =
    rawSorted === 'asc' || rawSorted === 'desc' ? rawSorted : false;
  const tooltip = sortTooltip(sortState);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={iconOnly ? accessibleTitle : undefined}
          onClick={column.getToggleSortingHandler()}
          className={cn(
            'flex w-full min-w-0 items-center gap-2 rounded text-left',
            'hover:bg-neutral-50/80 -mx-0.5 px-0.5',
            iconOnly ? 'justify-end' : 'justify-between'
          )}
        >
          {!iconOnly && (
            <div className="min-w-0 flex-1 text-left">{children}</div>
          )}
          <SortDirectionIcon sortState={sortState} />
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Compact sort control for use beside other header controls (e.g. column menu).
 */
export function TanstackColumnSortIconButton<TData>({
  column,
}: {
  column: Column<TData, unknown>;
}) {
  if (!column.getCanSort()) {
    return null;
  }

  const rawSorted = column.getIsSorted();
  const sortState: ColumnSortVisualState =
    rawSorted === 'asc' || rawSorted === 'desc' ? rawSorted : false;
  const tooltip = sortTooltip(sortState);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          aria-label={tooltip}
          onClick={e => {
            column.getToggleSortingHandler()?.(e);
          }}
        >
          <SortDirectionIcon sortState={sortState} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
